/**
 * Sidebar content for the student portal.
 *
 * Extracted from `PortalLayout` so the layout file stays focused on shell
 * concerns (mobile drawer, main scroll, language toggle).
 */
import { Link, NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  House,
  GraduationCap,
  DownloadSimple,
  Calendar,
  Lightbulb,
  LinkSimple,
  WarningCircle,
  ChartLineUp,
  Receipt,
  UserCircle,
  Headset,
  SignOut,
} from "@phosphor-icons/react";
import LanguageToggle from "../layout/LanguageToggle";

const LOGO_MARK = "/logomark.png";

const NAV_HOME = { to: "/portal", icon: House, key: "home", end: true };

const NAV_FORMATION = [
  { to: "/portal/courses", icon: GraduationCap, key: "courses" },
  { to: "/portal/resources", icon: DownloadSimple, key: "resources" },
];

const NAV_GESTION = [
  { to: "/portal/planning", icon: Calendar, key: "planning" },
  { to: "/portal/tracking", icon: ChartLineUp, key: "tracking" },
  { to: "/portal/ideas", icon: Lightbulb, key: "ideas" },
  { to: "/portal/leaks", icon: WarningCircle, key: "leaks" },
  { to: "/portal/adonislinks", icon: LinkSimple, key: "adonislinks" },
];

const NAV_BOTTOM = [
  { to: "/portal/invoices", icon: Receipt, key: "invoices" },
  { to: "/portal/support", icon: Headset, key: "support" },
  { to: "/portal/account", icon: UserCircle, key: "account" },
];

const SidebarLink = ({ to, icon: Icon, label, end, onClick }) => (
  <NavLink
    to={to}
    end={end}
    onClick={onClick}
    data-testid={`portal-nav-${to.split("/").pop() || "home"}`}
    className={({ isActive }) =>
      `group flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
        isActive
          ? "bg-slate-900 text-white shadow-sm"
          : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
      }`
    }
  >
    {({ isActive }) => (
      <>
        <Icon
          size={18}
          weight={isActive ? "fill" : "regular"}
          className="shrink-0"
        />
        <span className="truncate">{label}</span>
      </>
    )}
  </NavLink>
);

const SectionTitle = ({ children }) => (
  <p className="px-3 !mt-7 mb-2 text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">
    {children}
  </p>
);

export const PortalSidebar = ({ user, onLogout, onItemClick }) => {
  const { t } = useTranslation();
  return (
    <div className="flex h-full flex-col">
      {/* Scrollable top region (logo + identity + nav) */}
      <div className="flex-1 min-h-0 overflow-y-auto -mx-2 px-2 [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-thumb]:bg-slate-200 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-track]:bg-transparent">
        {/* Logo → public home */}
        <Link
          to="/"
          onClick={onItemClick}
          data-testid="portal-logo-home"
          className="flex items-center gap-3 px-2 py-2 mb-6"
        >
          <img src={LOGO_MARK} alt="Adonis Agency" className="h-9 w-9" />
          <div className="leading-tight">
            <p className="font-heading text-sm font-bold tracking-tight text-slate-900">
              Adonis Agency
            </p>
            <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">
              {t("portal.label")}
            </p>
          </div>
        </Link>

        {/* Signed in as */}
        <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 mb-6">
          <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">
            {t("portal.signed_in_as")}
          </p>
          <p className="mt-1 text-sm font-bold text-slate-900 truncate">
            {user.first_name || user.email}
          </p>
          <p className="text-xs text-slate-500 truncate">{user.email}</p>
        </div>

        {/* Nav */}
        <nav className="space-y-1">
          <SidebarLink
            to={NAV_HOME.to}
            icon={NAV_HOME.icon}
            label={t(`portal.nav.${NAV_HOME.key}`)}
            end={NAV_HOME.end}
            onClick={onItemClick}
          />

          <SectionTitle>{t("portal.section_formation")}</SectionTitle>
          {NAV_FORMATION.map((item) => (
            <SidebarLink
              key={item.to}
              to={item.to}
              icon={item.icon}
              label={t(`portal.nav.${item.key}`)}
              onClick={onItemClick}
            />
          ))}

          <SectionTitle>{t("portal.section_management")}</SectionTitle>
          {NAV_GESTION.map((item) => (
            <SidebarLink
              key={item.to}
              to={item.to}
              icon={item.icon}
              label={t(`portal.nav.${item.key}`)}
              onClick={onItemClick}
            />
          ))}
        </nav>
      </div>

      {/* Pinned bottom group: Invoices, Support, Account */}
      <div className="shrink-0 pt-6 mt-4 border-t border-slate-200 space-y-1">
        {NAV_BOTTOM.map((item) => (
          <SidebarLink
            key={item.to}
            to={item.to}
            icon={item.icon}
            label={t(`portal.nav.${item.key}`)}
            onClick={onItemClick}
          />
        ))}
        <div className="mt-7 mb-2 flex items-center justify-between gap-2">
          <LanguageToggle />
          <button
            type="button"
            data-testid="portal-logout"
            onClick={onLogout}
            className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-4 py-2 text-xs font-medium text-white hover:-translate-y-0.5 transition-all"
          >
            <SignOut size={14} weight="bold" />
            {t("portal.logout")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PortalSidebar;
